import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { fetchWithV2Fallback } from "../lib/v2";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { FileText, RefreshCw, AlertTriangle, Info, Loader2, CloudOff, CheckCircle2 } from "lucide-react";

export default function Policy() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("loading"); // "connected" | "offline" | "loading"

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setConnectionStatus("loading");
    try {
      const result = await fetchWithV2Fallback("/v2/my-policy", "/backoffice/my-policy");
      setPolicy(result.data);
      setConnectionStatus("connected");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setError("policy_not_found");
        setConnectionStatus("connected");
      } else if (status === 503) {
        setError("service_unavailable");
        setConnectionStatus("offline");
      } else {
        setError("generic");
        setConnectionStatus("offline");
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="policy-loading">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error === "policy_not_found") {
    return (
      <div className="space-y-6" data-testid="policy-not-found">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Minha Policy</h1>
            <p className="text-sm text-zinc-500 mt-1">Limites e taxas definidos pelo Backoffice</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-amber-800">Policy ainda não configurada</p>
            <p className="text-sm text-amber-700 mt-1">
              O administrador do Backoffice precisa configurar sua policy antes que os limites
              sejam exibidos aqui. Entre em contato com o suporte Zemo.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="policy-error">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Minha Policy</h1>
            {connectionStatus === "offline" && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs text-amber-700" data-testid="policy-offline-badge">
                <CloudOff className="h-3 w-3" />
                <span>Backoffice indisponível</span>
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="policy-retry-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Tentar novamente
          </Button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-700">
            {error === "service_unavailable"
              ? "Serviço do Backoffice temporariamente indisponível. Tente novamente em alguns instantes."
              : "Erro ao carregar policy."}
          </p>
        </div>
      </div>
    );
  }

  const { fee_schedule, defaults, limits } = policy || {};

  return (
    <div className="space-y-6" data-testid="policy-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Minha Policy</h1>
          <p className="text-sm text-zinc-500 mt-1">Limites e taxas definidos pelo Backoffice</p>
        </div>
        <div className="flex items-center gap-3">
          {connectionStatus === "connected" && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-700" data-testid="policy-connected-badge">
              <CheckCircle2 className="h-3 w-3" />
              <span>Backoffice conectado</span>
            </div>
          )}
          <Badge variant="outline" data-testid="policy-version-badge">
            <FileText className="h-3 w-3 mr-1" />
            Versão {policy?.version || "—"}
          </Badge>
          <Button variant="outline" size="sm" onClick={load} data-testid="policy-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
          </Button>
        </div>
      </div>

      {/* Fee Schedule */}
      <div className="bg-white border border-[var(--zemo-border)] rounded-lg" data-testid="policy-fees-section">
        <div className="px-6 py-4 border-b border-[var(--zemo-border)]">
          <h2 className="text-base font-semibold">Taxas (Fee Schedule)</h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <PolicyField label="Taxa padrão (%)" value={fee_schedule?.max_default_tax_pct} min={fee_schedule?.min_default_tax_pct} testid="fee-tax" />
          <PolicyField label="TAC fixo (R$)" value={fee_schedule?.max_fixed_tac_brl} min={fee_schedule?.min_fixed_tac_brl} testid="fee-tac-fixed" />
          <PolicyField label="TAC variável (%)" value={fee_schedule?.max_variable_tac_pct} min={fee_schedule?.min_variable_tac_pct} testid="fee-tac-var" />
          <PolicyField label="Dias floating" value={fee_schedule?.max_floating_days} min={fee_schedule?.min_floating_days} testid="fee-floating" />
        </div>
      </div>

      {/* Defaults */}
      <div className="bg-white border border-[var(--zemo-border)] rounded-lg" data-testid="policy-defaults-section">
        <div className="px-6 py-4 border-b border-[var(--zemo-border)]">
          <h2 className="text-base font-semibold">Valores Padrão</h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <SingleField label="Taxa padrão (%)" value={defaults?.default_tax_pct} testid="def-tax" />
          <SingleField label="TAC fixo padrão (R$)" value={defaults?.default_fixed_tac_brl} testid="def-tac-fixed" />
          <SingleField label="TAC variável padrão (%)" value={defaults?.default_variable_tac_pct} testid="def-tac-var" />
          <SingleField label="Floating padrão (dias)" value={defaults?.default_floating_days} testid="def-floating" />
          <SingleField label="Multa mora (%)" value={defaults?.default_late_fee_pct} testid="def-late-fee" />
          <SingleField label="Juros (%)" value={defaults?.default_interest_pct} testid="def-interest" />
        </div>
      </div>

      {/* Limits */}
      <div className="bg-white border border-[var(--zemo-border)] rounded-lg" data-testid="policy-limits-section">
        <div className="px-6 py-4 border-b border-[var(--zemo-border)]">
          <h2 className="text-base font-semibold">Limites Operacionais</h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <SingleField label="Auto-aprovação até (R$)" value={limits?.auto_approve_limit_brl} prefix="R$ " testid="lim-auto-approve" />
          <SingleField label="Prazo máximo (dias)" value={limits?.max_term_days} testid="lim-max-term" />
          <SingleField label="Prazo mínimo (dias)" value={limits?.min_term_days} testid="lim-min-term" />
        </div>
      </div>

      {/* Contract Template Info */}
      {policy?.contract_template_id && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-testid="policy-contract-info">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800">Template de Contrato</p>
            <p className="text-xs text-blue-700 mt-0.5 font-mono">{policy.contract_template_id}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function PolicyField({ label, value, min, testid }) {
  return (
    <div className="bg-zinc-50 rounded-md p-3" data-testid={testid}>
      <p className="text-xs text-zinc-500 mb-1">{label}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-semibold text-zinc-800">{min ?? "—"}</span>
        <span className="text-xs text-zinc-400">a</span>
        <span className="text-sm font-semibold text-zinc-800">{value ?? "—"}</span>
      </div>
    </div>
  );
}

function SingleField({ label, value, prefix = "", testid }) {
  return (
    <div className="bg-zinc-50 rounded-md p-3" data-testid={testid}>
      <p className="text-xs text-zinc-500 mb-1">{label}</p>
      <p className="text-sm font-semibold text-zinc-800">{prefix}{value ?? "—"}</p>
    </div>
  );
}
